import { useEffect, useState } from "react";
import { api } from "../api/client.js";
import { MetricCard } from "../components/MetricCard.js";
import { MetricSection } from "../components/MetricSection.js";

interface ListKpisResponse {
  kpis: Array<{
    metric: string;
    current: number | null;
    previous: number | null;
    sparkline: Array<{ day: string; value: number | null }>;
  }>;
}

type Tab = "actions" | "sync" | "negatives";

interface NegativeCandidate {
  ad_group_id: number;
  campaign_id: number;
  query: string;
  clicks: number;
  cost_rub: number;
  conversions: number;
  days_period: number;
}

interface NegativesResponse {
  candidates: NegativeCandidate[];
  total_cost_rub: number;
  total_clicks: number;
  action_log_ids: number[];
  period_days: number;
  thresholds: { min_clicks: number; max_conversions: number; min_cost_rub: number };
}

interface SyncLogEntry {
  id: number;
  resource: string;
  operation: string;
  fetched_count: number;
  upserted_count: number;
  api_units_used: number | null;
  api_units_remaining: number | null;
  started_at: string;
  finished_at: string | null;
  error_text: string | null;
}

interface ActionLogEntry {
  id: number;
  actor: "agent" | "human";
  action_type: string;
  target_kind: string;
  target_id: number | null;
  layer: string | null;
  hypothesis_id: number | null;
  params_before: object | null;
  params_after: object | null;
  reason_text: string | null;
  reverted_by_action_id: number | null;
  created_at: string;
}

const formatMSK = (ts: string) =>
  new Date(ts).toLocaleString("ru-RU", {
    timeZone: "Europe/Moscow",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });

export function ActionLogPage() {
  const [tab, setTab] = useState<Tab>("actions");
  const [bar, setBar] = useState<ListKpisResponse | null>(null);

  // KPI-bar: сводка по списку (Task 22). Backend stub в Task 14a, реальные числа в 14b.
  useEffect(() => {
    api<ListKpisResponse>("/api/metrics/list-kpis/actions")
      .then(setBar)
      .catch(() => setBar(null));
  }, []);

  return (
    <div>
      {bar && bar.kpis.length > 0 && (
        <MetricSection title="Сводка" cols={4}>
          {bar.kpis.slice(0, 4).map((k, i) => (
            <MetricCard
              key={i}
              metricId="cost_rub"
              current={k.current ?? null}
              previous={k.previous ?? null}
              comparisonLabel="vs прошлый период"
              sparkline={[]}
            />
          ))}
        </MetricSection>
      )}
      <h2 style={{ margin: 0, fontSize: "1.5rem" }}>Журнал действий</h2>
      <p style={{ color: "var(--text-dim)", margin: "0 0 1.5rem" }}>
        {tab === "actions"
          ? "Решения агента (классификация, разрешения, откаты)"
          : tab === "sync"
            ? "Sync-операции (тянем данные из Я.Директа и Метрики)"
            : "Кандидаты на минус-фразы — поисковые запросы с тратой бюджета без конверсий"}
      </p>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 0, borderBottom: "1px solid var(--border)", marginBottom: "1.5rem" }}>
        {(
          [
            { id: "actions" as const, label: "Действия агента" },
            { id: "sync" as const, label: "Sync-операции" },
            { id: "negatives" as const, label: "Минус-фразы кандидаты" },
          ]
        ).map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: "0.6rem 1rem",
              color: tab === t.id ? "var(--text)" : "var(--text-dim)",
              cursor: "pointer",
              borderBottom: tab === t.id ? "2px solid var(--green)" : "2px solid transparent",
              fontSize: "0.85rem",
              fontWeight: tab === t.id ? 600 : 500,
              border: "none",
              background: "transparent",
              borderRadius: 0,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "actions" ? <ActionsTab /> : tab === "sync" ? <SyncTab /> : <NegativesTab />}
    </div>
  );
}

function NegativesTab() {
  const [period, setPeriod] = useState(7);
  const [minClicks, setMinClicks] = useState(5);
  const [minCost, setMinCost] = useState(100);
  const [data, setData] = useState<NegativesResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [logged, setLogged] = useState<string | null>(null);

  function load(method: "GET" | "POST") {
    setBusy(true);
    setErr(null);
    setLogged(null);
    const url = method === "GET"
      ? `/api/negative-keywords/candidates?period_days=${period}&min_clicks=${minClicks}&min_cost_rub=${minCost}`
      : "/api/negative-keywords/log-candidates";
    api<NegativesResponse>(url, {
      method,
      body: method === "POST"
        ? JSON.stringify({ period_days: period, min_clicks: minClicks, min_cost_rub: minCost })
        : undefined,
    })
      .then((d) => {
        setData(d);
        if (method === "POST") setLogged(`Записано в журнал: ${d.action_log_ids.length} кандидатов`);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : "Ошибка"))
      .finally(() => setBusy(false));
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: "0.7rem",
          alignItems: "center",
          marginBottom: "1rem",
          flexWrap: "wrap",
          padding: "0.7rem 0.9rem",
          background: "var(--bg-elevated)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          fontSize: "0.82rem",
        }}
      >
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <span style={{ color: "var(--text-dim)" }}>Период (д):</span>
          <input
            type="number"
            min={1}
            max={60}
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            style={{
              background: "var(--bg)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              borderRadius: 4,
              padding: "0.25rem 0.4rem",
              width: 60,
              fontSize: "0.82rem",
            }}
          />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <span style={{ color: "var(--text-dim)" }}>Мин. кликов:</span>
          <input
            type="number"
            min={1}
            value={minClicks}
            onChange={(e) => setMinClicks(Number(e.target.value))}
            style={{
              background: "var(--bg)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              borderRadius: 4,
              padding: "0.25rem 0.4rem",
              width: 60,
              fontSize: "0.82rem",
            }}
          />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <span style={{ color: "var(--text-dim)" }}>Мин. трата (₽):</span>
          <input
            type="number"
            min={0}
            step={10}
            value={minCost}
            onChange={(e) => setMinCost(Number(e.target.value))}
            style={{
              background: "var(--bg)",
              color: "var(--text)",
              border: "1px solid var(--border)",
              borderRadius: 4,
              padding: "0.25rem 0.4rem",
              width: 80,
              fontSize: "0.82rem",
            }}
          />
        </label>
        <button
          onClick={() => load("GET")}
          disabled={busy}
          style={{
            padding: "0.35rem 0.8rem",
            background: "var(--bg)",
            color: "var(--text)",
            border: "1px solid var(--border)",
            borderRadius: 6,
            cursor: busy ? "wait" : "pointer",
            fontSize: "0.82rem",
          }}
        >
          {busy ? "..." : "Превью"}
        </button>
        <button
          onClick={() => {
            if (confirm("Записать кандидатов в action_log? Это log-only (без отправки в Я.Директ).")) load("POST");
          }}
          disabled={busy || !data || data.candidates.length === 0}
          style={{
            padding: "0.35rem 0.8rem",
            background: "var(--green)",
            color: "var(--bg)",
            border: "none",
            borderRadius: 6,
            cursor: busy ? "wait" : "pointer",
            fontSize: "0.82rem",
            fontWeight: 600,
          }}
        >
          Записать в журнал
        </button>
      </div>

      {err && <div style={{ color: "var(--red)", marginBottom: "0.7rem" }}>Ошибка: {err}</div>}
      {logged && (
        <div
          style={{
            marginBottom: "0.7rem",
            padding: "0.5rem 0.8rem",
            background: "rgba(74,222,128,0.08)",
            border: "1px solid var(--green)",
            borderRadius: 6,
            color: "var(--green)",
            fontSize: "0.82rem",
          }}
        >
          {logged}
        </div>
      )}

      {data && (
        <div style={{ color: "var(--text-dim)", fontSize: "0.78rem", marginBottom: "0.7rem" }}>
          {data.candidates.length} кандидатов · общий cost {Math.round(data.total_cost_rub).toLocaleString("ru-RU")} ₽ · {data.total_clicks} кликов · пороги {data.thresholds.min_clicks}+ кликов / ≤{data.thresholds.max_conversions} конв / ≥{data.thresholds.min_cost_rub} ₽
        </div>
      )}

      {data && data.candidates.length === 0 && (
        <div style={{ color: "var(--text-dim)", padding: "2rem", textAlign: "center", background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 10 }}>
          Кандидатов нет — либо нет search-queries за период, либо все запросы конвертят. Попробуй увеличить период или снизить порог кликов.
        </div>
      )}

      {data && data.candidates.length > 0 && (
        <table>
          <thead>
            <tr>
              <th>Запрос</th>
              <th className="right">Кликов</th>
              <th className="right">Cost ₽</th>
              <th className="right">Конверсий</th>
              <th>Группа</th>
              <th>Кампания</th>
            </tr>
          </thead>
          <tbody>
            {data.candidates.map((c, i) => (
              <tr key={i}>
                <td className="mono">{c.query}</td>
                <td className="right mono">{c.clicks}</td>
                <td className="right mono">{c.cost_rub.toFixed(0)}</td>
                <td className="right mono dim">{c.conversions}</td>
                <td className="mono dim">{c.ad_group_id}</td>
                <td className="mono dim">{c.campaign_id}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ActionsTab() {
  const [entries, setEntries] = useState<ActionLogEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ entries: ActionLogEntry[] }>("/api/action-log?limit=100")
      .then((d) => setEntries(d.entries))
      .catch((e) => setError((e as Error).message));
  }, []);

  if (error) return <div style={{ color: "var(--red)" }}>Ошибка: {error}</div>;
  if (!entries) return <div style={{ color: "var(--text-dim)" }}>Загрузка...</div>;

  if (entries.length === 0) {
    return (
      <div style={{ color: "var(--text-dim)", padding: "2rem", textAlign: "center", background: "var(--bg-elevated)", border: "1px solid var(--border)", borderRadius: 10 }}>
        Действий пока нет. Появятся после первой классификации или решения.
      </div>
    );
  }

  return (
    <table>
      <thead>
        <tr>
          <th>Время МСК</th>
          <th>Actor</th>
          <th>Тип</th>
          <th>Цель</th>
          <th>Слой</th>
          <th>Параметры</th>
          <th>Причина</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <tr key={e.id}>
            <td className="mono dim">{formatMSK(e.created_at)}</td>
            <td>
              <span className={`badge badge-${e.actor === "agent" ? "green" : "blue"}`}>
                {e.actor}
              </span>
            </td>
            <td className="mono">{e.action_type}</td>
            <td className="dim">
              {e.target_kind}
              {e.target_id ? <span className="mono"> · {e.target_id}</span> : null}
            </td>
            <td>{e.layer ? <span className="badge badge-purple">{e.layer}</span> : <span className="dim">—</span>}</td>
            <td className="mono dim" style={{ fontSize: "0.7rem" }}>
              {e.params_before || e.params_after
                ? `${JSON.stringify(e.params_before ?? {}).slice(0, 30)} → ${JSON.stringify(e.params_after ?? {}).slice(0, 30)}`
                : "—"}
            </td>
            <td style={{ color: "var(--text-dim)" }}>
              {e.reason_text ?? "—"}
              {e.reverted_by_action_id !== null && (
                <span className="badge badge-amber" style={{ marginLeft: "0.5rem" }}>
                  REVERTED
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function SyncTab() {
  const [entries, setEntries] = useState<SyncLogEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<{ entries: SyncLogEntry[] }>("/api/sync-log?limit=100")
      .then((d) => setEntries(d.entries))
      .catch((e) => setError((e as Error).message));
  }, []);

  if (error) return <div style={{ color: "var(--red)" }}>Ошибка: {error}</div>;
  if (!entries) return <div style={{ color: "var(--text-dim)" }}>Загрузка...</div>;

  return (
    <table>
      <thead>
        <tr>
          <th>Время МСК</th>
          <th>Ресурс</th>
          <th>Операция</th>
          <th className="right">Fetched</th>
          <th className="right">Upserted</th>
          <th className="right">API units</th>
          <th>Длительность</th>
          <th>Статус</th>
        </tr>
      </thead>
      <tbody>
        {entries.map((e) => {
          const duration = e.finished_at
            ? Math.round(new Date(e.finished_at).getTime() - new Date(e.started_at).getTime())
            : null;
          return (
            <tr key={e.id}>
              <td className="mono dim">{formatMSK(e.started_at)}</td>
              <td><span className="badge badge-blue">{e.resource}</span></td>
              <td className="dim">{e.operation}</td>
              <td className="right mono">{e.fetched_count}</td>
              <td className="right mono">{e.upserted_count}</td>
              <td className="right mono">{e.api_units_used ?? "—"}</td>
              <td className="mono dim">{duration !== null ? `${duration}ms` : "..."}</td>
              <td>
                {e.error_text ? (
                  <span className="badge badge-red">ERROR</span>
                ) : e.finished_at ? (
                  <span className="badge badge-green">OK</span>
                ) : (
                  <span className="badge badge-amber">RUNNING</span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
